import { useCallback, useEffect, useMemo, useState } from 'react'
import { Loader2, AlertTriangle, Info, Search } from 'lucide-react'
import { apiGet, cachedGet } from '../../lib/api.js'
import { apiErrorText } from '../../lib/apiError.js'
import { fmtThaiDate } from '../../lib/datetime.js'
import EmployeePicker from '../../components/employee/EmployeePicker.jsx'
import { useEmployeeFilter } from '../../components/employee/EmployeeFilter.jsx'

const FIELD = 'rounded-xl border border-slate-200 bg-white px-3 py-2 text-[13px] text-slate-800 outline-none focus:border-brand-400 focus:ring-4 focus:ring-brand-500/10'

/**
 * พนักงาน › เอกสาร › ทะเบียนใบรับรอง — every หนังสือรับรองเงินเดือน /
 * หนังสือรับรองการทำงาน the staff have issued themselves.
 *
 * 🔑 THIS IS THE CONTROL, AND IT IS DELIBERATELY READ-ONLY. Staff issue these
 * without approval, because a certificate only restates facts the CRM already
 * holds and an approval step would be HR retyping its own data (owner's decision,
 * 2026-08-26). What replaces the approval is this register plus the serial number
 * printed on every document: when a bank rings to verify "HR-2026-8-42", this is
 * where HR reads it back, and a pattern worth asking about is visible here.
 *
 * 🚨 THERE MUST NEVER BE AN APPROVE BUTTON HERE. Adding one would reintroduce the
 * queue the feature exists to avoid, at ten branches' worth of volume.
 *
 * ⚠️ Every row carries the salary figure that was printed, so this page is gated
 * HR/ADMIN — with payroll, NOT with หนังสือเตือน. GM is held by four branch
 * managers as well as the two owners, and /api/hr/salary already refuses them; the
 * tab is hidden in DocsLayout, _middleware.js refuses /api/hr/employee-document for
 * them, and Xano checks it a third time.
 *
 * `salary_amount` is the figure FROZEN at issue time, not a live read. If payroll is
 * edited afterwards the letter in the employee's hand still says what it said, and
 * verification has to agree with the paper.
 */

const DOC_LABEL = {
  salary_cert: 'หนังสือรับรองเงินเดือน',
  employment_letter: 'หนังสือรับรองการทำงาน',
}
const PURPOSE_LABEL = {
  general: 'ใช้ทั่วไป',
  bank: 'ยื่นขอสินเชื่อ',
  rental: 'เช่าที่พัก',
  visa: 'ยื่นขอวีซ่า',
  other: 'อื่นๆ',
}

const fmtMoney = (n) => (Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

export default function EmployeeDocument() {
  const [rows, setRows] = useState([])
  const [employees, setEmployees] = useState([])
  const [branches, setBranches] = useState([])
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState('')

  const [branchId, setBranchId] = useState(0)
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [q, setQ] = useState('')
  // เอกสาร's own person, shared by its three tabs only — see EmployeeFilter.jsx.
  const [employeeId, setEmployeeId] = useEmployeeFilter('docs')

  // Shared cachedGet entries with the rest of the staff area — same params, so this
  // page adds no round-trip of its own for the two reference lists.
  useEffect(() => {
    cachedGet('/api/hr/employees', { per_page: 500 })
      .then((r) => setEmployees(Array.isArray(r) ? r : (r?.items || [])))
      .catch(() => {})
    cachedGet('/api/branches')
      .then((r) => setBranches(Array.isArray(r) ? r : (r?.items || [])))
      .catch(() => {})
  }, [])

  const load = useCallback(async () => {
    setLoading(true)
    setErr('')
    try {
      const params = {}
      if (branchId) params.branch_id = branchId
      if (from) params.from = from
      if (to) params.to = to
      const r = await apiGet('/api/hr/employee-document', params)
      setRows(Array.isArray(r) ? r : (r?.items || []))
    } catch (e) {
      setErr(apiErrorText(e, 'โหลดทะเบียนไม่สำเร็จ'))
    } finally {
      setLoading(false)
    }
  }, [branchId, from, to])

  useEffect(() => { load() }, [load])

  const empName = useMemo(() => {
    const m = new Map()
    for (const e of employees) m.set(Number(e.id), e)
    return m
  }, [employees])

  const branchName = useMemo(() => {
    const m = new Map()
    for (const b of branches) m.set(Number(b.id), b.name || b.name_short || '')
    return m
  }, [branches])

  // Name / serial search stays client-side: the register is a few hundred rows at
  // most and the upstream has no text index on it.
  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase()
    return rows.filter((r) => {
      if (employeeId && String(r.employee_id) !== String(employeeId)) return false
      if (!needle) return true
      const e = empName.get(Number(r.employee_id))
      return `${e?.name || ''} ${e?.code || ''} ${r.serial || ''}`.toLowerCase().includes(needle)
    })
  }, [rows, q, employeeId, empName])

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-2 text-[12.5px] text-slate-600 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5">
        <Info size={15} className="mt-0.5 flex-none text-slate-400" />
        <span>
          พนักงานออกเอกสารเหล่านี้ได้เองทันทีโดยไม่ต้องขออนุมัติ — หน้านี้คือทะเบียนสำหรับตรวจสอบย้อนหลัง
          เมื่อธนาคารหรือหน่วยงานโทรมาสอบถาม ให้ตรวจจาก <span className="font-medium">เลขที่เอกสาร</span>
        </span>
      </div>

      <div className="flex flex-wrap items-end gap-2">
        <label className="flex flex-col gap-1">
          <span className="text-[12px] text-slate-500">สาขา</span>
          <select className={FIELD} value={branchId} onChange={(e) => setBranchId(Number(e.target.value))}>
            <option value={0}>ทั้งหมด</option>
            {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[12px] text-slate-500">ตั้งแต่</span>
          <input type="date" className={FIELD} value={from} onChange={(e) => setFrom(e.target.value)} />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[12px] text-slate-500">ถึง</span>
          <input type="date" className={FIELD} value={to} onChange={(e) => setTo(e.target.value)} />
        </label>
        {/* A <div>, not a <label>: a label wrapping the picker's button would forward
            every click inside it to that button and re-toggle the dropdown. */}
        <div className="flex flex-col gap-1">
          <span className="text-[12px] text-slate-500">พนักงาน</span>
          {/* activeOnly={false}: a certificate outlives the job — a bank calls about
              someone who has since left. */}
          <EmployeePicker value={employeeId} onChange={setEmployeeId} allowAll activeOnly={false} />
        </div>
        <label className="flex flex-col gap-1 flex-1 min-w-[180px]">
          <span className="text-[12px] text-slate-500">ค้นหา ชื่อ / รหัส / เลขที่เอกสาร</span>
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input className={`${FIELD} w-full pl-8`} value={q} onChange={(e) => setQ(e.target.value)} placeholder="เช่น HR-2026-8-42" />
          </div>
        </label>
      </div>

      {err && (
        <div className="flex items-start gap-2 text-[13px] text-rose-700 bg-rose-50 border border-rose-200 rounded-xl px-3 py-2.5">
          <AlertTriangle size={15} className="mt-0.5 flex-none" /><span>{err}</span>
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-[13px] text-slate-500 py-8">
          <Loader2 size={15} className="animate-spin" /> กำลังโหลด…
        </div>
      ) : shown.length === 0 ? (
        <div className="text-[13px] text-slate-500 py-8 text-center">ยังไม่มีการออกเอกสารในช่วงที่เลือก</div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-slate-200/80 bg-white">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="text-left text-slate-500 border-b border-slate-100">
                <Th>วันที่</Th><Th>เลขที่</Th><Th>พนักงาน</Th><Th>สาขา</Th>
                <Th>เอกสาร</Th><Th>วัตถุประสงค์</Th><Th right>เงินเดือนที่ระบุ</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {shown.map((r) => {
                const e = empName.get(Number(r.employee_id))
                return (
                  <tr key={r.id} className={r.rendered === false ? 'bg-rose-50/40' : ''}>
                    <Td nowrap>{fmtThaiDate(r.created_at)}</Td>
                    <Td nowrap>
                      <span className="tabular-nums">{r.serial || '—'}</span>
                      {/* A failed render still burned its serial. Shown, never hidden:
                          a register that silently dropped them would not reconcile. */}
                      {r.rendered === false && <span className="ml-1.5 text-[11px] text-rose-600">ไม่สำเร็จ</span>}
                    </Td>
                    <Td>{e ? `${e.name}${e.code ? ` (${e.code})` : ''}` : `#${r.employee_id}`}</Td>
                    <Td>{branchName.get(Number(r.branch_id)) || '—'}</Td>
                    <Td>{DOC_LABEL[r.doc] || r.doc}</Td>
                    <Td>
                      {PURPOSE_LABEL[r.purpose] || r.purpose}
                      {r.purpose === 'other' && r.purpose_note ? <span className="text-slate-500"> — {r.purpose_note}</span> : null}
                    </Td>
                    <Td right nowrap>
                      {r.doc === 'salary_cert'
                        ? <span className="tabular-nums">{fmtMoney(r.salary_amount)}</span>
                        : <span className="text-slate-400">—</span>}
                    </Td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function Th({ children, right }) {
  return <th className={`px-3 py-2.5 font-medium whitespace-nowrap ${right ? 'text-right' : ''}`}>{children}</th>
}
function Td({ children, right, nowrap }) {
  return <td className={`px-3 py-2.5 text-slate-700 ${right ? 'text-right' : ''} ${nowrap ? 'whitespace-nowrap' : ''}`}>{children}</td>
}
